import { useCallback } from 'react';
import { useFile, useSelection, useVisualizer, useUI } from '../context/AppContext.jsx';
import { convertWaypointsToWorldCoords } from '../utils/coordinateTransforms.js';
import { extractNodes } from '../utils/nodeUtils.js';
import { getWaypointIndicesToMirror } from '../services/MirrorService.js';

export function useChangeFeature() {
  const {
    parser,
    currentWaypoints,
    setCurrentWaypoints,
    currentFeatures,
    nodes,
    setNodes,
  } = useFile();
  const { multiSelectedIndices } = useSelection();
  const { visualizer } = useVisualizer();
  const { showStatus } = useUI();

  const applyFeatureChange = useCallback((scope, newFeatureIndex, transformMode, nodeIndex = null) => {
    if (!currentFeatures || currentFeatures.length === 0) {
      showStatus('No features loaded. Please load an .installation file', 'error');
      return;
    }

    const newFeature = currentFeatures[newFeatureIndex];

    // Determine which waypoints to change using the service
    const { indices: waypointIndices, error } = getWaypointIndicesToMirror(
      scope,
      currentWaypoints,
      multiSelectedIndices,
      nodes,
      nodeIndex
    );

    if (error) {
      showStatus(error, 'error');
      return;
    }

    try {
      // The parser works node-by-node on the XML, so a node's via-point, Pattern
      // origin/X-direction and SearchPartL touch points move with its waypoint
      // rather than being left behind in the old frame.
      const result = parser.changeFeature(
        waypointIndices,
        newFeature,
        currentFeatures,
        transformMode
      );

      // Re-read from the XML so the UI reflects exactly what was written,
      // including poses this hook never touched directly.
      const updatedWaypoints = parser.extractWaypoints();
      setCurrentWaypoints(updatedWaypoints);
      setNodes(extractNodes(updatedWaypoints));

      if (visualizer) {
        const waypointsForVis = convertWaypointsToWorldCoords(updatedWaypoints, currentFeatures);
        visualizer.visualizeWaypoints(waypointsForVis, false); // false = don't center camera
      }

      const extras = [];
      if (result.viaPoints) extras.push(`${result.viaPoints} via-point(s)`);
      if (result.patternPoses) extras.push(`${result.patternPoses} pattern pose(s)`);
      if (result.searchPoses) extras.push(`${result.searchPoses} search pose(s)`);
      if (result.suppressed) extras.push(`${result.suppressed} suppressed node(s)`);
      const detail = extras.length ? ` (also moved ${extras.join(', ')})` : '';

      if (result.warnings.length > 0) {
        result.warnings.forEach((message) => console.warn('Change feature:', message));
        showStatus(
          `Changed feature to ${newFeature.name} for ${result.converted} waypoint(s)${detail}. `
          + `${result.warnings.length} warning(s) - see console.`,
          'warning'
        );
      } else {
        showStatus(
          `Changed feature to ${newFeature.name} for ${result.converted} waypoint(s)${detail}`,
          'success'
        );
      }
    } catch (error) {
      console.error('Error changing feature:', error);
      showStatus(`Error: ${error.message}`, 'error');
    }
  }, [parser, visualizer, currentWaypoints, setCurrentWaypoints, currentFeatures, multiSelectedIndices, nodes, setNodes, showStatus]);

  return { applyFeatureChange };
}
